import React, { useState, useEffect } from 'react';
import { FaSearch, FaUser, FaCalendarAlt, FaEdit, FaTrash, FaTimes, FaHistory, FaUsers, FaClock, FaCheckCircle, FaTimesCircle, FaInfoCircle, FaClipboardList, FaCalendarPlus, FaCalendarMinus, FaEnvelope, FaFingerprint, FaExclamationCircle, FaTag, FaPhone, FaBookReader } from 'react-icons/fa';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import 'react-tabs/style/react-tabs.css';
import { collection, query, getDocs, where, orderBy, limit, doc, getDoc } from 'firebase/firestore';
import { db } from '../../../../firebase/config';
import { getStorage, getDownloadURL, ref } from 'firebase/storage';
import SharedHeader from '../../../../components/SharedHeader';
import DashboardFooter from '../../../../components/DashboardFooter';
import './subsc_log.css';

// # Main SubscriptionLogs component with tabs for different views
const SubscriptionLogs = () => {
  // # Initialize Firebase storage
  const storage = getStorage();
  
  // # State for active tab index
  const [tabIndex, setTabIndex] = useState(0);
  
  // # State for general logs
  const [generalLogs, setGeneralLogs] = useState([]);
  const [filteredGeneralLogs, setFilteredGeneralLogs] = useState([]);
  const [generalSearchTerm, setGeneralSearchTerm] = useState('');
  const [isLoadingGeneral, setIsLoadingGeneral] = useState(true);
  const [generalError, setGeneralError] = useState(null);
  
  // # State for user logs
  const [userLogs, setUserLogs] = useState([]);
  const [filteredUserLogs, setFilteredUserLogs] = useState([]);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  
  // # State for selected user modal
  const [selectedUser, setSelectedUser] = useState(null);
  const [userSubscriptionLogs, setUserSubscriptionLogs] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoadingUserLogs, setIsLoadingUserLogs] = useState(false);
  const [userLogsError, setUserLogsError] = useState(null);
  
  // # Add new state for user data cache
  const [userDataCache, setUserDataCache] = useState({});
  
  // # Function to fetch user data
  const fetchUserData = async (uid) => {
    if (userDataCache[uid]) {
      return userDataCache[uid];
    }

    try {
      const userDocRef = doc(db, 'users', uid);
      const userDocSnap = await getDoc(userDocRef);
      
      if (userDocSnap.exists()) {
        const userData = userDocSnap.data();
        const formattedData = {
          displayName: [
            userData.firstName || '',
            userData.middleName || '',
            userData.lastName || ''
          ].filter(Boolean).join(' ') || 'N/A',
          username: userData.username ? `@${userData.username}` : 'N/A',
          email: userData.email || 'N/A',
          uid: uid,
          phone: userData.phoneNumber ? 
            `${userData.selectedCountry?.code || ''} ${userData.phoneNumber}` : 'N/A'
        };
        
        // # Cache the user data
        setUserDataCache(prev => ({
          ...prev,
          [uid]: formattedData
        }));
        
        return formattedData;
      }
      
      return {
        displayName: 'N/A',
        username: 'N/A',
        email: 'N/A',
        uid: uid,
        phone: 'N/A'
      };
    } catch (error) {
      console.error(`Error fetching user data for ${uid}:`, error);
      return {
        displayName: 'Error loading data',
        username: 'N/A',
        email: 'N/A',
        uid: uid,
        phone: 'N/A'
      };
    }
  };
  
  // # Fetch general subscription logs on component mount
  useEffect(() => {
    const fetchGeneralLogs = async () => {
      try {
        setIsLoadingGeneral(true);
        setGeneralError(null);
        
        // # Get the last 6 months of logs (use current date, not future dates)
        const months = [];
        const now = new Date();
        
        for (let i = 0; i < 6; i++) {
          const month = new Date(now.getFullYear(), now.getMonth() - i, 1);
          const yearMonth = `${month.getFullYear()}_${(month.getMonth() + 1).toString().padStart(2, '0')}`;
          months.push(yearMonth);
        }
        
        let allLogs = [];
        let accessErrors = 0;
        
        // # Fetch logs for each month
        for (const yearMonth of months) {
          try {
            // # Get the document directly using doc() and getDoc() instead of query
            const logDocRef = doc(db, 'subscription_logs', yearMonth);
            const logDocSnap = await getDoc(logDocRef);
            
            if (logDocSnap.exists()) {
              const data = logDocSnap.data();
              
              // # Process each subscription in the document
              Object.entries(data).forEach(([subscriptionId, subscriptionData]) => {
                // # Process each change in the subscription
                if (subscriptionData.changes) {
                  Object.entries(subscriptionData.changes).forEach(([timestamp, change]) => {
                    // # Convert the timestamp format correctly
                    const formattedTimestamp = timestamp.replace(/_/g, (match, offset) => {
                      // # Replace underscores with appropriate characters based on position
                      if (offset === 13 || offset === 16) return ':';
                      if (offset === 10) return 'T';
                      if (offset === 19) return '.';
                      return match;
                    });
                    
                    allLogs.push({
                      id: `${yearMonth}_${subscriptionId}_${timestamp}`,
                      yearMonth,
                      subscriptionId,
                      timestamp: formattedTimestamp,
                      uid: subscriptionData.uid,
                      subscriptionType: subscriptionData.subscription_type,
                      changeType: change.change_type,
                      madeBy: change.made_by,
                      metadata: change.change_metadata || {}
                    });
                  });
                }
              });
            }
          } catch (error) {
            console.error(`Error fetching logs for ${yearMonth}:`, error);
            // # Count access errors to show appropriate message
            if (error.code === 'permission-denied') {
              accessErrors++;
            }
          }
        }
        
        // # Sort logs by timestamp (newest first)
        allLogs.sort((a, b) => {
          // # Parse the ISO string dates for proper comparison
          return new Date(b.timestamp) - new Date(a.timestamp);
        });
        
        // # After fetching logs, fetch user data for each unique user
        const uniqueUserIds = [...new Set(allLogs.map(log => log.uid))];
        const userDataPromises = uniqueUserIds.map(uid => fetchUserData(uid));
        await Promise.all(userDataPromises);
        
        setGeneralLogs(allLogs);
        setFilteredGeneralLogs(allLogs);
        
        // # Set error message if all months had access errors
        if (accessErrors === months.length) {
          setGeneralError("You don't have permission to access subscription logs. Please check your admin privileges.");
        } else if (allLogs.length === 0) {
          setGeneralError("No subscription logs found for the last 6 months.");
        }
        
        setIsLoadingGeneral(false);
      } catch (error) {
        console.error('Error fetching general logs:', error);
        setGeneralError("Failed to load subscription logs. Please try again later.");
        setIsLoadingGeneral(false);
      }
    };
    
    fetchGeneralLogs();
  }, []);
  
  // # Fetch users with subscription logs
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setIsLoadingUsers(true);
        
        // # Get unique user IDs from general logs
        const uniqueUserIds = [...new Set(generalLogs.map(log => log.uid))];
        
        // # Fetch user details for each unique user ID
        const usersData = [];
        
        for (const uid of uniqueUserIds) {
          try {
            // # Get user document directly
            const userDocRef = doc(db, 'users', uid);
            const userDocSnap = await getDoc(userDocRef);
            
            if (userDocSnap.exists()) {
              const userData = userDocSnap.data();
              
              // # Construct display name from available fields
              let displayName = 'Unknown User';
              
              // # Try different name combinations based on what's available
              if (userData.displayName) {
                // # Use displayName if it exists
                displayName = userData.displayName;
              } else if (userData.firstName) {
                // # Construct from firstName, middleName, lastName
                displayName = [
                  userData.firstName || '',
                  userData.middleName || '',
                  userData.lastName || ''
                ].filter(Boolean).join(' ');
              } else if (userData.username) {
                // # Use username if no name is available
                displayName = `@${userData.username}`;
              }
              
              // # Get user profile picture if available
              let profilePicUrl = null;
              if (userData.profilePicture) {
                try {
                  const storageRef = ref(storage, `profile_pictures/${uid}`);
                  profilePicUrl = await getDownloadURL(storageRef);
                } catch (error) {
                  console.log('No profile picture found for user:', uid);
                }
              }
              
              usersData.push({
                uid,
                displayName: displayName,
                email: userData.email || 'No Email',
                username: userData.username || '',
                profilePicUrl
              });
            } else {
              // # Add placeholder for users without documents
              usersData.push({
                uid,
                displayName: `User ${uid.substring(0, 6)}...`,
                email: 'No Email',
                username: '',
                profilePicUrl: null
              });
            }
          } catch (error) {
            console.error(`Error fetching user data for ${uid}:`, error);
            // # Add placeholder for users with errors
            usersData.push({
              uid,
              displayName: `User ${uid.substring(0, 6)}...`,
              email: 'Error loading user data',
              username: '',
              profilePicUrl: null
            });
          }
        }
        
        setUserLogs(usersData);
        setFilteredUserLogs(usersData);
        setIsLoadingUsers(false);
      } catch (error) {
        console.error('Error fetching users:', error);
        setIsLoadingUsers(false);
      }
    };
    
    if (generalLogs.length > 0) {
      fetchUsers();
    } else {
      setIsLoadingUsers(false);
    }
  }, [generalLogs]);
  
  // # Filter general logs based on search term
  useEffect(() => {
    if (generalSearchTerm.trim() === '') {
      setFilteredGeneralLogs(generalLogs);
    } else {
      const term = generalSearchTerm.toLowerCase();
      const filtered = generalLogs.filter(log => {
        // # Check log fields
        const logMatches = 
          log.subscriptionId.toLowerCase().includes(term) ||
          log.uid.toLowerCase().includes(term) ||
          log.subscriptionType.toLowerCase().includes(term) ||
          log.changeType.toLowerCase().includes(term);
        
        // # Check user data fields if available in cache
        const userData = userDataCache[log.uid];
        const userDataMatches = userData ? (
          (userData.displayName && userData.displayName.toLowerCase().includes(term)) ||
          (userData.email && userData.email.toLowerCase().includes(term)) ||
          (userData.username && userData.username.toLowerCase().includes(term)) ||
          (userData.phone && userData.phone.toLowerCase().includes(term))
        ) : false;
        
        return logMatches || userDataMatches;
      });
      setFilteredGeneralLogs(filtered);
    }
  }, [generalSearchTerm, generalLogs, userDataCache]);
  
  // # Filter user logs based on search term
  useEffect(() => {
    if (userSearchTerm.trim() === '') {
      setFilteredUserLogs(userLogs);
    } else {
      const term = userSearchTerm.toLowerCase();
      const filtered = userLogs.filter(user => 
        user.displayName.toLowerCase().includes(term) ||
        user.email.toLowerCase().includes(term) ||
        user.uid.toLowerCase().includes(term) ||
        (user.username && user.username.toLowerCase().includes(term))
      );
      setFilteredUserLogs(filtered);
    }
  }, [userSearchTerm, userLogs]);
  
  // # Handle general search input change
  const handleGeneralSearchChange = (e) => {
    setGeneralSearchTerm(e.target.value);
  };
  
  // # Handle user search input change
  const handleUserSearchChange = (e) => {
    setUserSearchTerm(e.target.value);
  };
  
  // # Handle user click to open modal with subscription logs
  const handleUserClick = async (user) => {
    setSelectedUser(user);
    setIsModalOpen(true);
    setIsLoadingUserLogs(true);
    setUserLogsError(null);
    
    try {
      // # Get logs for the selected user (use current date, not future dates)
      const months = [];
      const now = new Date();
      
      for (let i = 0; i < 12; i++) {
        const month = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const yearMonth = `${month.getFullYear()}_${(month.getMonth() + 1).toString().padStart(2, '0')}`;
        months.push(yearMonth);
      }
      
      let userLogs = [];
      let accessErrors = 0;
      
      // # Fetch logs for each month
      for (const yearMonth of months) {
        try {
          // # Get the document directly using doc() and getDoc()
          const logDocRef = doc(db, 'subscription_logs', yearMonth);
          const logDocSnap = await getDoc(logDocRef);
          
          if (logDocSnap.exists()) {
            const data = logDocSnap.data();
            
            // # Process each subscription in the document that belongs to the user
            Object.entries(data).forEach(([subscriptionId, subscriptionData]) => {
              if (subscriptionData.uid === user.uid) {
                // # Process each change in the subscription
                if (subscriptionData.changes) {
                  Object.entries(subscriptionData.changes).forEach(([timestamp, change]) => {
                    // # Convert the timestamp format correctly
                    const formattedTimestamp = timestamp.replace(/_/g, (match, offset) => {
                      // # Replace underscores with appropriate characters based on position
                      if (offset === 13 || offset === 16) return ':';
                      if (offset === 10) return 'T';
                      if (offset === 19) return '.';
                      return match;
                    });
                    
                    userLogs.push({
                      id: `${yearMonth}_${subscriptionId}_${timestamp}`,
                      yearMonth,
                      subscriptionId,
                      timestamp: formattedTimestamp,
                      subscriptionType: subscriptionData.subscription_type,
                      changeType: change.change_type,
                      madeBy: change.made_by,
                      metadata: change.change_metadata || {}
                    });
                  });
                }
              }
            });
          }
        } catch (error) {
          console.error(`Error fetching user logs for ${yearMonth}:`, error);
          if (error.code === 'permission-denied') {
            accessErrors++;
          }
        }
      }
      
      // # Sort logs by timestamp (newest first)
      userLogs.sort((a, b) => {
        // # Parse the ISO string dates for proper comparison
        return new Date(b.timestamp) - new Date(a.timestamp);
      });
      
      setUserSubscriptionLogs(userLogs);
      
      // # Set error message if all months had access errors
      if (accessErrors === months.length) {
        setUserLogsError("You don't have permission to access user subscription logs. Please check your admin privileges.");
      } else if (userLogs.length === 0) {
        setUserLogsError("No subscription logs found for this user.");
      }
      
      setIsLoadingUserLogs(false);
    } catch (error) {
      console.error('Error fetching user logs:', error);
      setUserLogsError("Failed to load user subscription logs. Please try again later.");
      setIsLoadingUserLogs(false);
    }
  };
  
  // # Close the user modal
  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedUser(null);
    setUserSubscriptionLogs([]);
    setUserLogsError(null);
  };
  
  // # Format timestamp for display
  const formatTimestamp = (timestamp) => {
    try {
      const date = new Date(timestamp);
      // # Check if date is valid before formatting
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      console.error('Error formatting timestamp:', error, timestamp);
      return 'Invalid Date';
    }
  };
  
  // # Format metadata value based on key and type
  const formatMetadataValue = (key, value) => {
    // # Handle date-related keys
    const dateKeys = ['startDate', 'endDate', 'oldEndDate', 'newEndDate', 'date', 'createdAt', 'updatedAt'];
    if (dateKeys.includes(key) && typeof value === 'string' && value.includes('T')) {
      return formatTimestamp(value);
    }

    // # Handle duration or period
    if (key.toLowerCase().includes('duration') || key.toLowerCase().includes('period')) {
      // Convert to days/months if the value is in milliseconds or seconds
      const num = parseInt(value);
      if (!isNaN(num)) {
        if (num > 86400000) return `${Math.round(num/86400000)} days`;
        if (num > 3600000) return `${Math.round(num/3600000)} hours`;
        if (num > 60000) return `${Math.round(num/60000)} minutes`;
      }
    }

    // # Handle boolean values
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }

    // # Handle objects
    if (typeof value === 'object' && value !== null) {
      return JSON.stringify(value, null, 2);
    }

    // # Default case
    return value.toString();
  };

  // # Get metadata display icon
  const getMetadataIcon = (key) => {
    if (key.includes('Date')) return <FaCalendarAlt className="subsc_log_metadata_icon" />;
    if (key.includes('start')) return <FaCalendarPlus className="subsc_log_metadata_icon" />;
    if (key.includes('end')) return <FaCalendarMinus className="subsc_log_metadata_icon" />;
    return null;
  };
  
  // # Format change type for display with icon
  const formatChangeTypeWithIcon = (changeType) => {
    switch (changeType) {
      case 'give':
        return <><FaCheckCircle className="subsc_log_action_icon success" /> Subscription Given</>;
      case 'extend':
        return <><FaHistory className="subsc_log_action_icon info" /> Subscription Extended</>;
      case 'deactivate':
        return <><FaTimesCircle className="subsc_log_action_icon danger" /> Subscription Deactivated</>;
      case 'edit':
        return <><FaEdit className="subsc_log_action_icon warning" /> Subscription Edited</>;
      case 'assign_mentor':
        return <><FaUsers className="subsc_log_action_icon primary" /> Mentor Assigned</>;
      case 'expired':
        return <><FaExclamationCircle className="subsc_log_action_icon expired" /> Subscription Expired</>;
      default:
        return <><FaInfoCircle className="subsc_log_action_icon" /> {changeType.charAt(0).toUpperCase() + changeType.slice(1)}</>;
    }
  };
  
  // # Check if value is a date or expired status
  const getMetadataItemType = (key, value) => {
    const dateKeys = ['startDate', 'endDate', 'oldEndDate', 'newEndDate', 'date', 'createdAt', 'updatedAt'];
    if (dateKeys.includes(key) && typeof value === 'string' && value.includes('T')) {
      return 'date';
    }
    if (key === 'reason' && value === 'subscription_expired') {
      return 'expired';
    }
    return 'default';
  };
  
  // # Get admin name from ID
  const getAdminName = async (adminId) => {
    try {
      const adminDocRef = doc(db, 'users', adminId);
      const adminDocSnap = await getDoc(adminDocRef);
      
      if (adminDocSnap.exists()) {
        const adminData = adminDocSnap.data();
        if (adminData.displayName) {
          return adminData.displayName;
        } else if (adminData.firstName) {
          return [
            adminData.firstName || '',
            adminData.middleName || '',
            adminData.lastName || ''
          ].filter(Boolean).join(' ');
        } else if (adminData.username) {
          return `@${adminData.username}`;
        }
      }
      
      return `Admin ${adminId.substring(0, 6)}...`;
    } catch (error) {
      console.error('Error fetching admin name:', error);
      return `Admin ${adminId.substring(0, 6)}...`;
    }
  };
  
  // # Check if value is a date
  const isDateValue = (key, value) => {
    const dateKeys = ['startDate', 'endDate', 'oldEndDate', 'newEndDate', 'date', 'createdAt', 'updatedAt'];
    return dateKeys.includes(key) && typeof value === 'string' && value.includes('T');
  };

  // # Add UserDataCell component for displaying user information
  const UserDataCell = ({ uid }) => {
    const userData = userDataCache[uid] || {};
    
    return (
      <div className="subsc_log_user_data">
        <div className="subsc_log_user_data_main">
          <span className="subsc_log_user_data_name">{userData.displayName}</span>
          <span className="subsc_log_user_data_username">{userData.username}</span>
        </div>
        <div className="subsc_log_user_data_details">
          <div className="subsc_log_user_data_item">
            <FaEnvelope className="subsc_log_user_data_icon" />
            <span>{userData.email}</span>
          </div>
          <div className="subsc_log_user_data_item">
            <FaFingerprint className="subsc_log_user_data_icon" />
            <span>{userData.uid}</span>
          </div>
          <div className="subsc_log_user_data_item">
            <FaPhone className="subsc_log_user_data_icon" />
            <span>{userData.phone}</span>
          </div>
        </div>
      </div>
    );
  };

  // # Format subscription type for display
  const formatSubscriptionType = (type) => {
    // # Remove underscores and convert to title case
    const formatted = type
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
    
    // # Special formatting for specific types
    switch (type.toLowerCase()) {
      case 'step1_library':
        return 'Step 1 Library';
      case 'step2_library':
        return 'Step 2 Library';
      case 'step1_mentorship':
        return 'Step 1 Mentorship';
      case 'step2_mentorship':
        return 'Step 2 Mentorship';
      case 'oet':
        return 'OET';
      case 'us_visa_counselling':
        return 'US Visa Counselling';
      default:
        return formatted;
    }
  };

  // # Update the subscription type display in the table row
  const renderSubscriptionType = (subscriptionType) => (
    <div className="subsc_log_subscription_type">
      <FaBookReader className="subsc_log_subscription_icon" />
      <span>{formatSubscriptionType(subscriptionType)}</span>
    </div>
  );

  return (
    <div className="subsc_log_container">
      <SharedHeader title="Subscription Logs" />
      
      <div className="subsc_log_content">
        <h1 className="subsc_log_title">Subscription Logs</h1>
        <p className="subsc_log_subtitle">View and search subscription activity logs</p>
        
        <Tabs 
          selectedIndex={tabIndex} 
          onSelect={index => setTabIndex(index)}
          className="subsc_log_tabs"
        >
          <TabList className="subsc_log_tablist">
            <Tab className="subsc_log_tab">
              <FaClipboardList /> General Log
            </Tab>
            <Tab className="subsc_log_tab">
              <FaUsers /> User Logs
            </Tab>
          </TabList>
          
          {/* # General Log Tab Panel */}
          <TabPanel>
            <div className="subsc_log_search_container">
              <div className="subsc_log_search_wrapper">
                <FaSearch className="subsc_log_search_icon" />
                <input
                  type="text"
                  className="subsc_log_search_input"
                  placeholder="Search by subscription ID, user data (name, email, username, phone), type, or action..."
                  value={generalSearchTerm}
                  onChange={handleGeneralSearchChange}
                />
              </div>
            </div>
            
            {isLoadingGeneral ? (
              <div className="subsc_log_loading">
                <div className="subsc_log_loading_spinner"></div>
                <p>Loading logs...</p>
              </div>
            ) : generalError ? (
              <div className="subsc_log_error">
                <FaTimesCircle className="subsc_log_error_icon" />
                <p>{generalError}</p>
              </div>
            ) : filteredGeneralLogs.length === 0 ? (
              <div className="subsc_log_no_results">
                <FaInfoCircle className="subsc_log_info_icon" />
                <p>No logs found</p>
              </div>
            ) : (
              <div className="subsc_log_table_container">
                <table className="subsc_log_table">
                  <thead>
                    <tr>
                      <th><FaClock /> Date & Time</th>
                      <th><FaUser /> User Data</th>
                      <th><FaClipboardList /> Subscription Type</th>
                      <th><FaEdit /> Action</th>
                      <th><FaInfoCircle /> Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredGeneralLogs.map((log) => (
                      <tr key={log.id}>
                        <td>
                          <div className="subsc_log_date">
                            <FaCalendarAlt className="subsc_log_date_icon" />
                            <span>{formatTimestamp(log.timestamp)}</span>
                          </div>
                        </td>
                        <td>
                          <UserDataCell uid={log.uid} />
                        </td>
                        <td>
                          {renderSubscriptionType(log.subscriptionType)}
                        </td>
                        <td>{formatChangeTypeWithIcon(log.changeType)}</td>
                        <td>
                          <div className="subsc_log_metadata">
                            {Object.entries(log.metadata).map(([key, value]) => (
                              <div 
                                key={key} 
                                className="subsc_log_metadata_item"
                                data-type={getMetadataItemType(key, value)}
                              >
                                <div className="subsc_log_metadata_key">
                                  {getMetadataIcon(key)}
                                  <span>{key.replace(/([A-Z])/g, ' $1').trim()}:</span>
                                </div>
                                <span className="subsc_log_metadata_value">
                                  {formatMetadataValue(key, value)}
                                </span>
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabPanel>
          
          {/* # User Logs Tab Panel */}
          <TabPanel>
            <div className="subsc_log_search_container">
              <div className="subsc_log_search_wrapper">
                <FaSearch className="subsc_log_search_icon" />
                <input
                  type="text"
                  className="subsc_log_search_input"
                  placeholder="Search by name, email, username, or user ID..."
                  value={userSearchTerm}
                  onChange={handleUserSearchChange}
                />
              </div>
            </div>
            
            {isLoadingUsers ? (
              <div className="subsc_log_loading">Loading users...</div>
            ) : generalError ? (
              <div className="subsc_log_error">{generalError}</div>
            ) : filteredUserLogs.length === 0 ? (
              <div className="subsc_log_no_results">No users found</div>
            ) : (
              <div className="subsc_log_users_grid">
                {filteredUserLogs.map((user) => (
                  <div 
                    key={user.uid} 
                    className="subsc_log_user_card"
                    onClick={() => handleUserClick(user)}
                  >
                    <div className="subsc_log_user_avatar">
                      {user.profilePicUrl ? (
                        <img 
                          src={user.profilePicUrl} 
                          alt={user.displayName} 
                          className="subsc_log_user_image"
                        />
                      ) : (
                        <FaUser className="subsc_log_user_icon" />
                      )}
                    </div>
                    <div className="subsc_log_user_info">
                      <h3 className="subsc_log_user_name">{user.displayName}</h3>
                      {user.username && (
                        <p className="subsc_log_user_username">@{user.username}</p>
                      )}
                      <p className="subsc_log_user_email">{user.email}</p>
                      <p className="subsc_log_user_id">{user.uid}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabPanel>
        </Tabs>
      </div>
      
      {/* # User Subscription Logs Modal */}
      {isModalOpen && selectedUser && (
        <div className="subsc_log_modal_overlay">
          <div className="subsc_log_modal">
            <div className="subsc_log_modal_header">
              <div className="subsc_log_modal_user">
                {selectedUser.profilePicUrl ? (
                  <img 
                    src={selectedUser.profilePicUrl} 
                    alt={selectedUser.displayName} 
                    className="subsc_log_modal_user_image"
                  />
                ) : (
                  <FaUser className="subsc_log_modal_user_icon" />
                )}
                <div className="subsc_log_modal_user_info">
                  <h2 className="subsc_log_modal_user_name">{selectedUser.displayName}</h2>
                  {selectedUser.username && (
                    <p className="subsc_log_modal_user_username">
                      <FaUser size={14} />
                      @{selectedUser.username}
                    </p>
                  )}
                  <p className="subsc_log_modal_user_email">
                    <FaEnvelope size={14} />
                    {selectedUser.email}
                  </p>
                  <p className="subsc_log_modal_user_id">
                    <FaFingerprint size={14} />
                    {selectedUser.uid}
                  </p>
                </div>
              </div>
              <button 
                className="subsc_log_modal_close" 
                onClick={closeModal}
                aria-label="Close modal"
              >
                <FaTimes />
              </button>
            </div>
            
            <div className="subsc_log_modal_content">
              <h3 className="subsc_log_modal_title">
                <FaHistory />
                Subscription History
              </h3>
              
              {isLoadingUserLogs ? (
                <div className="subsc_log_loading">
                  <div className="subsc_log_loading_spinner"></div>
                  <p>Loading subscription history...</p>
                </div>
              ) : userLogsError ? (
                <div className="subsc_log_error">
                  <FaExclamationCircle className="subsc_log_error_icon" />
                  <p>{userLogsError}</p>
                </div>
              ) : userSubscriptionLogs.length === 0 ? (
                <div className="subsc_log_no_results">
                  <FaInfoCircle className="subsc_log_info_icon" />
                  <p>No subscription history found</p>
                </div>
              ) : (
                <div className="subsc_log_modal_table_container">
                  <table className="subsc_log_modal_table">
                    <thead>
                      <tr>
                        <th><FaCalendarAlt /> Date & Time</th>
                        <th><FaTag /> Subscription Type</th>
                        <th><FaEdit /> Action</th>
                        <th><FaInfoCircle /> Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {userSubscriptionLogs.map((log) => (
                        <tr key={log.id}>
                          <td>
                            <div className="subsc_log_date">
                              <FaCalendarAlt className="subsc_log_date_icon" />
                              <span>{formatTimestamp(log.timestamp)}</span>
                            </div>
                          </td>
                          <td>
                            {renderSubscriptionType(log.subscriptionType)}
                          </td>
                          <td>{formatChangeTypeWithIcon(log.changeType)}</td>
                          <td>
                            <div className="subsc_log_metadata">
                              {Object.entries(log.metadata).map(([key, value]) => (
                                <div 
                                  key={key} 
                                  className="subsc_log_metadata_item"
                                  data-type={getMetadataItemType(key, value)}
                                >
                                  <div className="subsc_log_metadata_key">
                                    {getMetadataIcon(key)}
                                    <span>{key.replace(/([A-Z])/g, ' $1').trim()}:</span>
                                  </div>
                                  <span className="subsc_log_metadata_value">
                                    {formatMetadataValue(key, value)}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      <DashboardFooter />
    </div>
  );
};

export default SubscriptionLogs;
